import { useRef, useState, type ChangeEvent } from "react";

interface LogoUploadProps {
  value: string;
  onChange: (value: string) => void;
  /** Libellé affiché au-dessus de l'aperçu. */
  label?: string;
  hint?: string;
}

const MAX_SIZE = 1024 * 1024; // 1 Mo
const ACCEPTED = ["image/png", "image/jpeg"];

/**
 * Téléversement d'un logo (stocké en data URL, en session uniquement).
 * Le logo alimente l'en-tête des PDF et impression des documents.
 */
export default function LogoUpload({ value, onChange, label = "Logo", hint }: LogoUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setError(null);
    if (!ACCEPTED.includes(file.type)) {
      setError("Format non pris en charge. Utilisez une image PNG ou JPG.");
      return;
    }
    if (file.size > MAX_SIZE) {
      setError("Image trop lourde (1 Mo maximum).");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") onChange(reader.result);
    };
    reader.onerror = () => setError("La lecture du fichier a échoué.");
    reader.readAsDataURL(file);
  };

  return (
    <div className="flex flex-col gap-3">
      <span className="text-sm font-label font-medium text-foreground-800">{label}</span>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-background-200 bg-background-100">
          {value ? (
            <img src={value} alt={label} title={label} className="h-full w-full object-contain" />
          ) : (
            <i className="ri-image-add-line text-2xl text-foreground-400" aria-hidden="true" />
          )}
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            <i className="ri-upload-2-line text-base" aria-hidden="true" />
            {value ? "Changer le logo" : "Téléverser un logo"}
          </button>
          {value && (
            <button
              type="button"
              onClick={() => {
                setError(null);
                onChange("");
              }}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-3.5 py-2 text-sm font-label font-medium text-foreground-600 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-delete-bin-line text-base" aria-hidden="true" />
              Retirer
            </button>
          )}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg"
        className="sr-only"
        onChange={handleFile}
      />
      {hint && !error && <p className="text-xs text-foreground-400">{hint}</p>}
      {error && (
        <p className="text-xs text-primary-700" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}